<template>
  <template v-if="helpers.isText(node)">
    <CMSTextNode :node="node" />
  </template>
  <template v-else-if="helpers.isBlock(node)">
    <component
      :is="getNodeData(node).component"
      :node="node"
      v-bind="{ ...getNodeData(node).props, ...attrs }"
    >
      <template
        v-for="(childNode, _index) in node.content"
        :key="_getId(_index)"
      >
        <CMSRichTextNode :node="childNode" />
      </template>
    </component>
  </template>
  <template v-else-if="helpers.isInline(node)">
    <component
      :is="getNodeData(node).component"
      :node="node"
      v-bind="{ ...getNodeData(node).props, ...attrs }"
    >
      <template
        v-for="(childNode, _index) in node.content"
        :key="_getId(_index)"
      >
        <template
          v-if="
            helpers.isInline(node) &&
            helpers.isInline(childNode) &&
            isInlineEntry(childNode)
          "
        >
          <CMSInlineEntry :node="childNode" />
        </template>
        <CMSRichTextNode v-else :node="childNode" />
      </template>
    </component>
  </template>
</template>

<script setup lang="ts">
import { BLOCKS, helpers, INLINES } from '@contentful/rich-text-types'
import type { Block, Inline, Text } from '@contentful/rich-text-types'
import { useAttrs } from 'vue'
import CMSInlineEntry from './CMSInlineEntry.vue'
import CMSTextNode from './CMSTextNode.vue'

// Props
defineProps<{
  node: Block | Inline | Text
}>()

const componentsList = {
  [BLOCKS.PARAGRAPH]: { component: 'CMSParagraphNode', props: {} },
  [BLOCKS.HEADING_1]: { component: 'CMSHeadingNode', props: { level: 1 } },
  [BLOCKS.HEADING_2]: { component: 'CMSHeadingNode', props: { level: 2 } },
  [BLOCKS.HEADING_3]: { component: 'CMSHeadingNode', props: { level: 3 } },
  [BLOCKS.HEADING_4]: { component: 'CMSHeadingNode', props: { level: 4 } },
  [BLOCKS.HEADING_5]: { component: 'CMSHeadingNode', props: { level: 5 } },
  [BLOCKS.HEADING_6]: { component: 'CMSHeadingNode', props: { level: 6 } },
  [BLOCKS.UL_LIST]: { component: 'CMSUnorderedListNode', props: {} },
  [BLOCKS.OL_LIST]: { component: 'CMSOrderedListNode', props: {} },
  [BLOCKS.LIST_ITEM]: { component: 'CMSListItemNode', props: {} },
  [BLOCKS.QUOTE]: { component: 'CMSQuoteNode', props: {} },
  [BLOCKS.HR]: { component: 'CMSHrNode', props: {} },
  [INLINES.HYPERLINK]: { component: 'CMSHyperlinkNode', props: {} },
  [INLINES.EMBEDDED_ENTRY]: { component: 'CMSInlineEntry', props: {} },
}

const _getId = (index: number) => `id-${index}`
const attrs = useAttrs()

function isInlineEntry(node: Block | Inline) {
  return node.nodeType === 'embedded-entry-inline'
}

function getNodeData(node: Block | Inline) {
  return componentsList[node.nodeType as keyof typeof componentsList]
}

defineExpose({
  componentsList,
  isInlineEntry,
  getNodeData,
})
</script>
